import { Box, SvgIcon, Typography, Link, IconButton, Tooltip } from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import VideocamIcon from "@mui/icons-material/Videocam";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import lock from '../../../assets/Images/lock.png';
import { COURSE_SECTION_TYPE_ID, renderTruncatedText } from "../../../core/Constant";
import { Actions } from "../../../core/modules/Actions";

const CONTENT_ACTION_LABEL = {
  [COURSE_SECTION_TYPE_ID.VIDEO]: "Watch",
  [COURSE_SECTION_TYPE_ID.QUIZ]: "Assess",
  [COURSE_SECTION_TYPE_ID.DOCUMENT]: "Read",
};

const ACTION_COLOR = "rgb(152, 52, 240)";
const COMPLETE_COLOR = "#16A34A";

const QuizBulbIcon = ({ sx }) => (
  <SvgIcon sx={sx} viewBox="0 0 24 24">
    <path
      d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z"
      fill="#FACC15"
    />
    <text x="12" y="12.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E293B" fontFamily="Arial, sans-serif">?</text>
  </SvgIcon>
);

const ContentTypeIcon = ({ contentType }) => {
  const sx = { width: "22px", height: "22px" };
  switch (Number(contentType)) {
    case COURSE_SECTION_TYPE_ID.VIDEO:
      return <VideocamIcon sx={{ ...sx, color: "#A855F7" }} />;
    case COURSE_SECTION_TYPE_ID.DOCUMENT:
      return <PictureAsPdfIcon sx={{ ...sx, color: "#EF4444" }} />;
    case COURSE_SECTION_TYPE_ID.QUIZ:
      return <QuizBulbIcon sx={sx} />;
    default:
      return null;
  }
};

const VideoListView = ({
  icon,
  onclick,
  size,
  loked = 0,
  heading,
  content,
  link,
  onPreviewClick,
  contentType = 0,
  item,
  index,
  fileName,
  isExpired = false
}) => {
  const dispatch = useDispatch();
  const [locked, setLock] = useState(true)
  const [isCompleted, setIsCompleted] = useState(false);
  const [updating, setUpdating] = useState(false);

  const actualContentType = Number(item?.content_type ?? contentType);

  useEffect(() => {
    setLock(item?.is_locked);
    setIsCompleted(item?.students_course_status?.length > 0);
  }, [item])

  const onToggleComplete = async () => {
    if (updating || !item?.id) return;
    const nextStatus = !isCompleted;
    setUpdating(true);
    try {
      const payload = await dispatch(Actions.course.markCourseContentCompletion({
        course_content_id: item.id,
        is_completed: nextStatus,
        courseId: item?.course_id,
      }));
      if (payload?.status_code == 1) {
        setIsCompleted(nextStatus);
      }
    } finally {
      setUpdating(false);
    }
  };

  const isAccessible = ((!locked) || loked) && !isExpired;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", alignSelf: "center" }}>
      {index === 0 && heading ? (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", mt: 2.5, mb: 1.5, pl: 1 }}>
          <Box
            sx={{
              border: "1.5px solid #cbd5e1",
              borderRadius: "24px",
              px: 2.5,
              py: 0.5,
              backgroundColor: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#1e293b",
                fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.5px",
              }}
            >
              Day {heading}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, height: "1.5px", backgroundColor: "#e2e8f0", ml: 2 }} />
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.25,
          pl: { xs: 1.5, sm: 6 },
          pr: 1,
          borderRadius: "8px",
          transition: "background-color 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(152, 52, 240, 0.03)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, mr: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, flexShrink: 0 }}>
            <ContentTypeIcon contentType={actualContentType} />
          </Box>

          <Typography
            sx={{
              fontSize: "15px",
              color: "#334155",
              fontWeight: 500,
              fontFamily: "'Montserrat', sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
            }}
          >
            {renderTruncatedText(content, { limit: 60 })}
          </Typography>
        </Box>

        {isAccessible ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, ml: 1, gap: 1 }}>
            <Link
              component="button"
              onClick={onPreviewClick}
              sx={{
                textDecoration: "none",
                "&:hover": {
                  "& .action-text": {
                    textDecoration: "underline",
                  }
                }
              }}
            >
              <Typography
                className="action-text"
                sx={{
                  fontSize: "14px",
                  color: ACTION_COLOR,
                  fontWeight: 600,
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {isCompleted ? "Completed" : (CONTENT_ACTION_LABEL[actualContentType] || "View")}
              </Typography>
            </Link>
            <Tooltip title={isCompleted ? "Mark as incomplete" : "Mark as complete"}>
              <span>
                <IconButton size="small" onClick={onToggleComplete} disabled={updating} sx={{ p: 0.5 }}>
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ color: COMPLETE_COLOR, fontSize: "22px" }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ color: COMPLETE_COLOR, fontSize: "22px" }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, ml: 1 }}>
            <img
              style={{ width: "20px", height: "20px", opacity: 0.7 }}
              src={lock}
              alt="locked"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VideoListView;
